// frontend/src/utils/geoUtils.js
//
// Geolocation acquisition helpers for workforce TimeLog flows (TIME-004a+).
// Pattern per docs/architecture/TIME-004a-clock-in-out-tech.md §7.3:
//   - enableHighAccuracy: true   — GPS over Wi-Fi/cell triangulation
//   - timeout: 10_000           — 10 second cap so the user is not stranded
//   - maximumAge: 0             — no stale fix; staff cannot reuse a position
//                                 captured elsewhere or earlier in the day

/**
 * Permission denial / unavailable / timeout codes wrapped into a stable shape
 * the calling component can switch on without inspecting the raw browser
 * GeolocationPositionError.
 */
export const GEO_ERRORS = {
  UNSUPPORTED: "geo_unsupported",
  PERMISSION_DENIED: "geo_permission_denied",
  POSITION_UNAVAILABLE: "geo_position_unavailable",
  TIMEOUT: "geo_timeout",
  UNKNOWN: "geo_unknown",
};

/**
 * Normalize a browser GeolocationPositionError code into our GEO_ERRORS enum.
 * @param {GeolocationPositionError} err
 * @returns {string}
 */
const normalizeGeoError = (err) => {
  if (!err) return GEO_ERRORS.UNKNOWN;
  // Spec values: 1 PERMISSION_DENIED, 2 POSITION_UNAVAILABLE, 3 TIMEOUT
  switch (err.code) {
    case 1:
      return GEO_ERRORS.PERMISSION_DENIED;
    case 2:
      return GEO_ERRORS.POSITION_UNAVAILABLE;
    case 3:
      return GEO_ERRORS.TIMEOUT;
    default:
      return GEO_ERRORS.UNKNOWN;
  }
};

/**
 * Acquire a one-shot high-accuracy GPS fix. Resolves with the contract the
 * clock-in / clock-out endpoints expect:
 *   { coordinates: { lat, lng }, accuracy }
 * Rejects with an Error whose `.code` is one of GEO_ERRORS.
 *
 * @returns {Promise<{ coordinates: { lat: number, lng: number }, accuracy: number }>}
 */
export const acquireLocation = () =>
  new Promise((resolve, reject) => {
    if (
      typeof navigator === "undefined" ||
      !navigator.geolocation ||
      typeof navigator.geolocation.getCurrentPosition !== "function"
    ) {
      const err = new Error("Geolocation is not supported by this browser.");
      err.code = GEO_ERRORS.UNSUPPORTED;
      reject(err);
      return;
    }

    navigator.geolocation.getCurrentPosition(
      (pos) => {
        resolve({
          coordinates: {
            lat: pos.coords.latitude,
            lng: pos.coords.longitude,
          },
          accuracy: pos.coords.accuracy,
        });
      },
      (err) => {
        const wrapped = new Error(err.message || "Geolocation failed.");
        wrapped.code = normalizeGeoError(err);
        wrapped.cause = err;
        reject(wrapped);
      },
      { enableHighAccuracy: true, timeout: 10_000, maximumAge: 0 }
    );
  });

/**
 * User-facing copy for each geolocation error code. Mirrors the design spec
 * (TIME-004 §"Edge cases" + permission-denied state) and the tech-doc error
 * table (§9).
 * @param {string} code
 * @returns {string}
 */
export const geoErrorCopy = (code) => {
  switch (code) {
    case GEO_ERRORS.UNSUPPORTED:
      return "This browser does not support location services. Please open the studio web app in Safari (iOS) or Chrome (Android).";
    case GEO_ERRORS.PERMISSION_DENIED:
      return "Location access required to clock in at the studio. Please enable location in your browser settings and try again.";
    case GEO_ERRORS.POSITION_UNAVAILABLE:
      return "We could not determine your location. Please check that location services are on, and try again.";
    case GEO_ERRORS.TIMEOUT:
      return "Locating you is taking longer than expected. Please try again.";
    default:
      return "We could not determine your location. Please try again.";
  }
};
